import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Button,
  Tabs,
  Tab,
  Table,
  Badge,
  Modal,
  ListGroup,
  Alert,
  Accordion // Ajout de Accordion
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faClock,
  faExpand,
  faCompress,
  faExclamationTriangle,
  faUsers,
  faBuilding,
  faHandshake,
  faChevronDown, // Pour l'accordéon
  faChevronUp // Pour l'accordéon
} from '@fortawesome/free-solid-svg-icons';

const formatTime = timeStr => {
  if (!timeStr) return '';
  return timeStr.substring(0, 5);
};

const formatParticipantName = p => {
  if (!p) return 'N/A';
  return (
    `${p.firstname || ''} ${p.lastname || ''}`.trim() || 'Participant Inconnu'
  );
};

const generateSalonTimeSlots = salon => {
  if (
    !salon.date ||
    !salon.start_time ||
    !salon.end_time ||
    !salon.slot_duration
  ) {
    return [];
  }

  const slots = [];
  const salonDate = salon.date.substring(0, 10);

  let currentTime = new Date(`${salonDate}T${salon.start_time}`);
  const endTime = new Date(`${salonDate}T${salon.end_time}`);
  const lunchStart = salon.lunch_break_start_time
    ? new Date(`${salonDate}T${salon.lunch_break_start_time}`)
    : null;
  const lunchEnd =
    lunchStart && salon.lunch_break_duration
      ? new Date(lunchStart.getTime() + salon.lunch_break_duration * 60000)
      : null;

  while (currentTime < endTime) {
    const slotStartTime = new Date(currentTime);
    const slotEndTime = new Date(
      currentTime.getTime() + salon.slot_duration * 60000
    );

    let isLunchBreak = false;
    if (lunchStart && lunchEnd) {
      if (
        (slotStartTime >= lunchStart && slotStartTime < lunchEnd) ||
        (slotEndTime > lunchStart && slotEndTime <= lunchEnd) ||
        (slotStartTime < lunchStart && slotEndTime > lunchEnd)
      ) {
        isLunchBreak = true;
      }
    }

    if (!isLunchBreak && slotEndTime <= endTime) {
      slots.push({
        start: formatTime(slotStartTime.toTimeString()),
        end: formatTime(slotEndTime.toTimeString())
      });
    }

    currentTime = new Date(currentTime.getTime() + salon.slot_duration * 60000);

    if (
      lunchStart &&
      lunchEnd &&
      currentTime >= lunchStart &&
      currentTime < lunchEnd
    ) {
      currentTime = new Date(lunchEnd);
    }
  }
  return slots;
};

const MeetingsCard = ({ salon, onSchedule, onEditMeeting }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeAccordionKey, setActiveAccordionKey] = useState(null); // Pour l'accordéon

  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  const timeSlots = useMemo(
    () => generateSalonTimeSlots(salon),
    [
      salon.date,
      salon.start_time,
      salon.end_time,
      salon.slot_duration,
      salon.lunch_break_start_time,
      salon.lunch_break_duration
    ]
  );

  const companyViewData = useMemo(() => {
    if (!salon.meetings || salon.meetings.length === 0) {
      return [];
    }
    const meetingsByCompanyParticipant = salon.meetings.reduce(
      (acc, meetingSlot) => {
        if (
          !meetingSlot.company_participants ||
          meetingSlot.company_participants.length === 0
        ) {
          return acc;
        }
        meetingSlot.company_participants.forEach(cp => {
          if (!cp || !cp.id) return;
          const participantId = cp.id;
          if (!acc[participantId]) {
            acc[participantId] = {
              participantId: cp.id,
              companyName: cp.company?.name || 'N/A',
              participantName: formatParticipantName(cp),
              schedule: {}
            };
            timeSlots.forEach(slot => {
              acc[participantId].schedule[slot.start] = null;
            });
          }
          const startTime = formatTime(meetingSlot.start_time);
          if (Object.hasOwn(acc[participantId].schedule, startTime)) {
            let partnerDisplay = 'Partenaire Indisponible';
            if (
              meetingSlot.partner_participants &&
              meetingSlot.partner_participants.length > 0
            ) {
              const firstPartnerParticipant =
                meetingSlot.partner_participants[0];
              partnerDisplay =
                firstPartnerParticipant.partner?.name ||
                formatParticipantName(firstPartnerParticipant) ||
                'Partenaire (détails manquants)';
            }
            acc[participantId].schedule[startTime] = {
              text: partnerDisplay,
              meetingId: meetingSlot.id
            };
          }
        });
        return acc;
      },
      {}
    );
    return Object.values(meetingsByCompanyParticipant).sort((a, b) =>
      `${a.companyName} ${a.participantName}`.localeCompare(
        `${b.companyName} ${b.participantName}`
      )
    );
  }, [salon.meetings, timeSlots]);

  const partnerViewData = useMemo(() => {
    if (!salon.meetings || salon.meetings.length === 0) {
      return [];
    }
    const meetingsByPartnerParticipant = salon.meetings.reduce(
      (acc, meetingSlot) => {
        if (
          !meetingSlot.partner_participants ||
          meetingSlot.partner_participants.length === 0
        ) {
          return acc;
        }
        meetingSlot.partner_participants.forEach(pp => {
          if (!pp || !pp.id) return;
          const participantId = pp.id;
          if (!acc[participantId]) {
            acc[participantId] = {
              participantId: pp.id,
              partnerName: pp.partner?.name || 'N/A',
              participantName: formatParticipantName(pp),
              schedule: {}
            };
            timeSlots.forEach(slot => {
              acc[participantId].schedule[slot.start] = null;
            });
          }
          const startTime = formatTime(meetingSlot.start_time);
          if (Object.hasOwn(acc[participantId].schedule, startTime)) {
            let companyDisplay = 'Entreprise Indisponible';
            if (
              meetingSlot.company_participants &&
              meetingSlot.company_participants.length > 0
            ) {
              const firstCompanyParticipant =
                meetingSlot.company_participants[0];
              companyDisplay =
                firstCompanyParticipant.company?.name ||
                formatParticipantName(firstCompanyParticipant) ||
                'Entreprise (détails manquants)';
            }
            acc[participantId].schedule[startTime] = {
              text: companyDisplay,
              meetingId: meetingSlot.id
            };
          }
        });
        return acc;
      },
      {}
    );
    return Object.values(meetingsByPartnerParticipant).sort((a, b) =>
      `${a.partnerName} ${a.participantName}`.localeCompare(
        `${b.partnerName} ${b.participantName}`
      )
    );
  }, [salon.meetings, timeSlots]);

  const meetingsByTimeSlot = useMemo(() => {
    if (!salon.meetings || salon.meetings.length === 0) {
      return {};
    }
    const grouped = salon.meetings.reduce((acc, m) => {
      const startTime = formatTime(m.start_time);
      if (!acc[startTime]) {
        acc[startTime] = [];
      }
      const companyParticipant = m.company_participants?.[0];
      const partnerParticipant = m.partner_participants?.[0];
      acc[startTime].push({
        id: m.id,
        endTime: formatTime(m.end_time),
        companyName: companyParticipant?.company?.name || 'N/A',
        companyParticipantName: formatParticipantName(companyParticipant),
        partnerName: partnerParticipant?.partner?.name || 'N/A',
        partnerParticipantName: formatParticipantName(partnerParticipant),
        rawMeeting: m
      });
      return acc;
    }, {});

    // Sort meetings within each timeslot by company name
    for (const time in grouped) {
      grouped[time].sort((a, b) => a.companyName.localeCompare(b.companyName));
    }
    return grouped;
  }, [salon.meetings]);

  const findMeetingById = meetingId => {
    return salon.meetings?.find(m => m.id === meetingId);
  };

  const handleCellClick = meetingData => {
    if (onEditMeeting && meetingData && meetingData.meetingId) {
      const meetingToEdit = findMeetingById(meetingData.meetingId);
      if (meetingToEdit) {
        onEditMeeting(meetingToEdit);
      }
    }
  };

  const handleListMeetingClick = rawMeeting => {
    if (onEditMeeting && rawMeeting) {
      onEditMeeting(rawMeeting);
    }
  };

  const schedulingIssues = salon.scheduling_issues || [];

  const stickyHeaderCellStyle = {
    position: 'sticky',
    zIndex: 2,
    backgroundColor: 'var(--falcon-200)'
  };

  const stickyBodyCellStyle = {
    position: 'sticky',
    zIndex: 1,
    backgroundColor: 'var(--falcon-body-bg)'
  };

  const cardContent = (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Rendez-vous</h5>
        <div>
          <Button
            variant="falcon-primary"
            size="sm"
            onClick={onSchedule}
            className="me-2"
            disabled={
              !salon.can_schedule &&
              (!salon.meetings || salon.meetings.length === 0)
            }
            title={
              !salon.can_schedule &&
              (!salon.meetings || salon.meetings.length === 0)
                ? "La planification ne peut être effectuée qu'après la date de fermeture des inscriptions ou si aucun RDV n'existe."
                : 'Générer / Re-générer le planning des rendez-vous'
            }
          >
            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
            {salon.meetings && salon.meetings.length > 0
              ? 'Re-planifier'
              : 'Planifier'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={toggleFullScreen}
            title={isFullScreen ? 'Quitter le plein écran' : 'Plein écran'}
          >
            <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} />
          </Button>
        </div>
      </div>

      {(!salon.meetings || salon.meetings.length === 0) && (
        <div className="text-center py-5">
          <FontAwesomeIcon
            icon={faCalendarAlt}
            size="3x"
            className="text-muted mb-3"
          />
          <p className="text-muted">
            {salon.can_schedule === false &&
            salon.closing_date &&
            new Date(salon.closing_date) > new Date()
              ? `La planification des rendez-vous sera possible après la date de fermeture des inscriptions (${new Date(
                  salon.closing_date
                ).toLocaleDateString('fr-FR')}).`
              : 'Aucun rendez-vous planifié pour ce salon.'}
          </p>
          {(!salon.meetings || salon.meetings.length === 0) &&
            salon.can_schedule !== false && (
              <p className="text-muted small">
                Cliquez sur "
                {salon.meetings && salon.meetings.length > 0
                  ? 'Re-planifier'
                  : 'Planifier'}
                " pour générer les rendez-vous.
              </p>
            )}
        </div>
      )}

      {salon.meetings && salon.meetings.length > 0 && (
        <Tabs
          defaultActiveKey="company-view"
          id="meetings-tabs"
          className="mt-3"
          unmountOnExit
        >
          <Tab
            eventKey="company-view"
            title={
              <>
                <FontAwesomeIcon icon={faBuilding} className="me-1" /> Vue
                Entreprises
              </>
            }
          >
            <div
              className="mt-3"
              style={{
                overflowX: 'auto',
                maxHeight: isFullScreen ? 'calc(100vh - 220px)' : '500px'
              }}
            >
              <Table bordered hover striped size="sm" className="fs--1">
                <thead className="bg-200 text-900">
                  <tr>
                    <th
                      style={{
                        ...stickyHeaderCellStyle,
                        left: 0,
                        minWidth: '170px'
                      }}
                    >
                      Entreprise
                    </th>
                    <th
                      style={{
                        ...stickyHeaderCellStyle,
                        left: '170px',
                        minWidth: '200px'
                      }}
                    >
                      Participant
                    </th>
                    {timeSlots.map(slot => (
                      <th
                        key={slot.start}
                        className="text-center"
                        style={{ minWidth: '120px' }}
                      >
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        {slot.start}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {companyViewData.length > 0 ? (
                    companyViewData.map(data => (
                      <tr key={data.participantId}>
                        <td
                          style={{
                            ...stickyBodyCellStyle,
                            left: 0,
                            minWidth: '170px'
                          }}
                        >
                          {data.companyName}
                        </td>
                        <td
                          style={{
                            ...stickyBodyCellStyle,
                            left: '170px',
                            minWidth: '200px'
                          }}
                        >
                          {data.participantName}
                        </td>
                        {timeSlots.map(slot => (
                          <td
                            key={`${data.participantId}-${slot.start}`}
                            className="text-center"
                            onClick={() =>
                              handleCellClick(data.schedule[slot.start])
                            }
                            style={{
                              cursor: data.schedule[slot.start]
                                ? 'pointer'
                                : 'default',
                              minWidth: '120px'
                            }}
                          >
                            {data.schedule[slot.start] ? (
                              <Badge bg="primary" className="p-1">
                                {data.schedule[slot.start].text}
                              </Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={2 + timeSlots.length}
                        className="text-center"
                      >
                        Aucun meeting à afficher.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Tab>

          <Tab
            eventKey="partner-view"
            title={
              <>
                <FontAwesomeIcon icon={faUsers} className="me-1" /> Vue
                Partenaires
              </>
            }
          >
            <div
              className="mt-3"
              style={{
                overflowX: 'auto',
                maxHeight: isFullScreen ? 'calc(100vh - 220px)' : '500px'
              }}
            >
              <Table bordered hover striped size="sm" className="fs--1">
                <thead className="bg-200 text-900">
                  <tr>
                    <th
                      style={{
                        ...stickyHeaderCellStyle,
                        left: 0,
                        minWidth: '170px'
                      }}
                    >
                      Partenaire
                    </th>
                    <th
                      style={{
                        ...stickyHeaderCellStyle,
                        left: '170px',
                        minWidth: '200px'
                      }}
                    >
                      Participant
                    </th>
                    {timeSlots.map(slot => (
                      <th
                        key={slot.start}
                        className="text-center"
                        style={{ minWidth: '120px' }}
                      >
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        {slot.start}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {partnerViewData.length > 0 ? (
                    partnerViewData.map(data => (
                      <tr key={data.participantId}>
                        <td
                          style={{
                            ...stickyBodyCellStyle,
                            left: 0,
                            minWidth: '170px'
                          }}
                        >
                          {data.partnerName}
                        </td>
                        <td
                          style={{
                            ...stickyBodyCellStyle,
                            left: '170px',
                            minWidth: '200px'
                          }}
                        >
                          {data.participantName}
                        </td>
                        {timeSlots.map(slot => (
                          <td
                            key={`${data.participantId}-${slot.start}`}
                            className="text-center"
                            onClick={() =>
                              handleCellClick(data.schedule[slot.start])
                            }
                            style={{
                              cursor: data.schedule[slot.start]
                                ? 'pointer'
                                : 'default',
                              minWidth: '120px'
                            }}
                          >
                            {data.schedule[slot.start] ? (
                              <Badge bg="info" className="p-1">
                                {data.schedule[slot.start].text}
                              </Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={2 + timeSlots.length}
                        className="text-center"
                      >
                        Aucun meeting à afficher.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Tab>

          <Tab
            eventKey="list-view"
            title={
              <>
                <FontAwesomeIcon icon={faHandshake} className="me-1" /> Liste
                Complète
              </>
            }
          >
            <div
              className="mt-3"
              style={{
                maxHeight: isFullScreen ? 'calc(100vh - 220px)' : '500px',
                overflowY: 'auto'
              }}
            >
              {Object.keys(meetingsByTimeSlot).length > 0 ? (
                <Accordion
                  activeKey={activeAccordionKey}
                  onSelect={k => setActiveAccordionKey(k)}
                  flush
                >
                  {timeSlots
                    .filter(slot => meetingsByTimeSlot[slot.start]?.length > 0)
                    .map((slot, index) => (
                      <Accordion.Item
                        eventKey={index.toString()}
                        key={slot.start}
                      >
                        <Accordion.Header>
                          <div className="d-flex justify-content-between w-100 align-items-center pe-2">
                            <span>
                              <FontAwesomeIcon
                                icon={faClock}
                                className="me-2"
                              />
                              {slot.start} - {slot.end}
                            </span>
                            <Badge pill bg="secondary">
                              {meetingsByTimeSlot[slot.start]?.length || 0} RDV
                            </Badge>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body className="p-0">
                          <Table hover striped size="sm" className="fs--1 mb-0">
                            <thead className="bg-200 text-900">
                              <tr>
                                <th>Entreprise</th>
                                <th>Participant Entreprise</th>
                                <th>Partenaire</th>
                                <th>Participant Partenaire</th>
                                <th className="text-end">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {meetingsByTimeSlot[slot.start].map(m => (
                                <tr key={m.id}>
                                  <td>{m.companyName}</td>
                                  <td>{m.companyParticipantName}</td>
                                  <td>{m.partnerName}</td>
                                  <td>{m.partnerParticipantName}</td>
                                  <td className="text-end">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() =>
                                        handleListMeetingClick(m.rawMeeting)
                                      }
                                    >
                                      Modifier
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                </Accordion>
              ) : (
                <Alert variant="info" className="mt-3">
                  Aucun rendez-vous planifié à lister.
                </Alert>
              )}
            </div>
          </Tab>

          <Tab
            eventKey="generation-issues"
            title={
              <>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-danger me-1"
                />
                Problèmes{' '}
                {schedulingIssues.length > 0 && (
                  <Badge pill bg="danger">
                    {schedulingIssues.length}
                  </Badge>
                )}
              </>
            }
          >
            <div
              className="mt-3"
              style={{
                maxHeight: isFullScreen ? 'calc(100vh - 220px)' : '500px',
                overflowY: 'auto'
              }}
            >
              {schedulingIssues.length > 0 ? (
                <ListGroup variant="flush">
                  {schedulingIssues.map(issue => (
                    <ListGroup.Item key={issue.id}>
                      <div className="d-flex w-100 justify-content-between">
                        <h6 className="mb-1 text-danger">
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="me-2"
                          />
                          {issue.issue_type}
                        </h6>
                        <small>
                          {new Date(issue.created_at).toLocaleString('fr-FR')}
                        </small>
                      </div>
                      <p className="mb-1 ms-4">{issue.message}</p>
                      {issue.details && (
                        <pre className="ms-4 p-2 bg-light border rounded fs--2">
                          <small className="text-muted">
                            Détails: {JSON.stringify(issue.details, null, 2)}
                          </small>
                        </pre>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <Alert variant="success" className="mt-3">
                  Aucun problème détecté lors de la dernière génération des
                  rendez-vous.
                </Alert>
              )}
            </div>
          </Tab>
        </Tabs>
      )}
    </>
  );

  if (isFullScreen) {
    return (
      <Modal
        show={isFullScreen}
        onHide={toggleFullScreen}
        dialogClassName="modal-fullscreen-xl-down"
        size="xl"
        fullscreen="xl-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Rendez-vous (Plein écran) - {salon.name || 'Salon'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: isFullScreen ? '1rem' : undefined }}>
          {cardContent}
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Card className="mt-3">
      <Card.Body>{cardContent}</Card.Body>
    </Card>
  );
};

MeetingsCard.propTypes = {
  salon: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    date: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    slot_duration: PropTypes.number,
    lunch_break_start_time: PropTypes.string,
    lunch_break_duration: PropTypes.number,
    can_schedule: PropTypes.bool,
    closing_date: PropTypes.string,
    meetings: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        start_time: PropTypes.string.isRequired,
        end_time: PropTypes.string.isRequired,
        company_participants: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number.isRequired,
            firstname: PropTypes.string,
            lastname: PropTypes.string,
            company: PropTypes.shape({ name: PropTypes.string })
          })
        ),
        partner_participants: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number.isRequired,
            firstname: PropTypes.string,
            lastname: PropTypes.string,
            partner: PropTypes.shape({ name: PropTypes.string })
          })
        )
      })
    ),
    scheduling_issues: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        issue_type: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired,
        created_at: PropTypes.string,
        details: PropTypes.object
      })
    ),
    company_participants: PropTypes.array
  }).isRequired,
  onSchedule: PropTypes.func.isRequired,
  onEditMeeting: PropTypes.func
};

export default MeetingsCard;
